import { useState } from "react";
import type { FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";
import { Button, Field, Input, Switch, errorMessage } from "@fortex/map";
import { PageFrame } from "../app/App";
import { useApp, useAuth } from "../app/context";

export function safeReturnUrl(value: string | null): string {
  if (value === null || !value.startsWith("/") || value.startsWith("//") || value.startsWith("/login")) return "/";

  return value;
}

export function LoginPage() {
  const { api, auth, labels } = useApp();
  const token = useAuth(a => a.token);
  const navigate = useNavigate();
  const location = useLocation();
  const returnUrl = safeReturnUrl(new URLSearchParams(location.search).get("returnUrl"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [stay, setStay] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  if (token !== null) return <Navigate to={returnUrl} replace />;

  const emailError = touched && email.trim().length === 0 ? labels.emailRequired : null;
  const passwordError = touched && password.length === 0 ? labels.passwordRequired : null;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);

    if (email.trim().length === 0 || password.length === 0) return;

    setBusy(true);
    setError(null);

    try {
      await auth.getState().signIn(api, email.trim(), password, stay);
      navigate(returnUrl, { replace: true });
    } catch (reason) {
      setError(errorMessage(reason, labels) || labels.loginFailed);
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageFrame title={labels.loginTitle} icon="login" narrow>
      <form className="fxapp-card fxapp-form" onSubmit={submit} noValidate>
        <h1 className="fxmap-display fxapp-title">{labels.loginTitle}</h1>
        <p className="fxmap-secondary">{labels.loginIntro}</p>
        <Field label={labels.email} required error={emailError}>
          <Input type="email" name="email" autoComplete="username" value={email} onChange={event => setEmail(event.target.value)} autoFocus />
        </Field>
        <Field label={labels.password} required error={passwordError}>
          <Input type="password" name="password" autoComplete="current-password" value={password} onChange={event => setPassword(event.target.value)} />
        </Field>
        <Switch checked={stay} onChange={setStay} label={labels.staySignedIn} showLabel name="staySignedIn" />
        {error !== null ? (
          <p className="fxapp-form-error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="fxapp-form-actions">
          <Button kind="primary" type="submit" label={labels.loginSubmit} busy={busy} data-fxmap="login-submit" />
        </div>
      </form>
    </PageFrame>
  );
}
